import React, { FC, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon, Modal, Title } from '@investoro/core';
import clsx from 'clsx';
import { UserActions } from 'store/User/Actions';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { userApiRequests } from 'utils/api-requests/user';
import { Platform, usePlatform } from 'utils/hooks/usePlatform';

import Button from 'components/atom/Button';
import Indicator from 'components/atom/Indicator';
import { IconEnum } from 'components/common/Icon';
import InputContainer from 'components/common/InputContainer';
import TextInput from 'components/common/Inputs/TextInput';
import { activeNotification } from 'components/functions/activeNotification';

const Container = styled.div`
    display: flex;
    width: 100%;
    min-height: 100vh;
    height: auto;

    align-items: center;
    justify-content: center;
`;

const LoginForm = styled.form`
    display: flex;
    flex-direction: column;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.25);
    padding: 25px;
    border-radius: 10px;
    gap: 30px;
    background: var(--color-white);
    margin: 0 10px;
    width: 100%;
    max-width: 500px;

    .icon {
        ${(props) => props.darkMode
            ? 'filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(79deg) brightness(105%) contrast(102%);'
            : ''};
    }
`;

const TitleWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const Logo = styled.img`
    max-width: 500px;
`;

const RegisterLink = styled.div`
    text-align: center;
    margin-top: 20px;
    font-size: var(--font-size-body-2);

    a {
        color: var(--color-primary);
        text-decoration: underline;
        cursor: pointer;

        &:hover {
            color: var(--color-primary-70);
        }
    }
`;

const ResetPasswordWrapper = styled.div`
    text-align: center;
    margin-top: 20px;
    font-size: var(--font-size-body-2);
    cursor: pointer;
    color: var(--color-primary);

    &:hover {
        color: var(--color-primary-70);
    }
`;

const IOSButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 400px;
`;

const IosButton = styled.div`
    cursor: pointer;
    width: 80%;
    color: var(--color-alt);
    font-weight: var(--font-weight);
    display: flex;
    align-items: center;
    gap: 22px;
    font-size: 15px;
    height: 50px;
    border: 2px solid var(--color-alt);
    border-radius: 10px;
    padding: 18px;

    .icon img {
        filter: invert(8%) sepia(100%) saturate(2448%) hue-rotate(208deg) brightness(96%) contrast(105%);
    }

    &:hover {
        background-color: var(--color-alt);
        color: var(--color-white);

        .icon img {
            filter: brightness(0) invert(1) !important;
        }
    }
`;

const ModalFormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 30px;
    align-items: center;
    padding: 40px;
    background-color: var(--color-white);

    input {
        min-width: 300px;
    }
`;

const LoginPage: FC = () => {
    const { user, dispatch } = useUser();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showLoginForm, setShowLoginForm] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const platform = usePlatform();
    const navigate = useNavigate();
    const [sendPasswordEmail, setSendPasswordEmail] = useState('');

    const handleSubmitForm = async (e: Event) => {
        e.preventDefault();
        await UserActions.logInUser(dispatch, email, password);
    };

    const checkIfUserIsLoggedIn = useCallback(async () => {
        await UserActions.loginByToken(dispatch);
    }, [dispatch]);

    useEffect(() => {
        checkIfUserIsLoggedIn();

        if (platform !== Platform.IOS) {
            setShowLoginForm(true);
        }
    }, [checkIfUserIsLoggedIn, platform]);

    const handleSendResetLink = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!sendPasswordEmail) {
            activeNotification('Pole nie może być puste', 'Podaj adres email', 'warning');

            return;
        }

        if (!emailRegex.test(sendPasswordEmail)) {
            activeNotification('Błąd', 'Podany adres email jest niepoprawny', 'warning');

            return;
        }

        const success = await userApiRequests.restorePasswordByEmail({ email: sendPasswordEmail });

        if (success) {
            setShowModal(false);
            setSendPasswordEmail('');
        }
    };

    if (platform === Platform.IOS && !showLoginForm) {
        return (
            <Container>
                <LoginForm darkMode={user.isDarkMode}>
                    <Logo src="/icons/investoro_pdf_logo.png" />
                    <IOSButtonsContainer>
                        <IosButton onClick={() => setShowLoginForm(true)}>
                            <Icon icon={IconEnum.PEOPLE} />
                            Zaloguj
                        </IosButton>
                        <IosButton onClick={() => navigate('/create-account-purchase-app-ios')}>
                            <Icon icon={IconEnum.WALLET} />
                            Załóż konto <br/>i wykup subskrypcję
                        </IosButton>
                    </IOSButtonsContainer>
                </LoginForm>
                {user.isLoading && <Indicator />}
            </Container>
        );
    }

    return (
        <>
            <Container>
                <LoginForm darkMode={user.isDarkMode} onSubmit={handleSubmitForm} >
                    <Logo src="/icons/investoro_pdf_logo.png" />

                    <TitleWrapper>
                        <Title>Logowanie</Title>
                    </TitleWrapper>

                    <InputContainer title={'Adres email'} icon={IconEnum.PEOPLE}>
                        <TextInput
                            type={'email'}
                            placeholder={'Podaj adres email...'}
                            onChange={({ target: { value } }) => setEmail(value)}
                            value={email}
                            required
                        />
                    </InputContainer>
                    <InputContainer title={'Hasło'} icon={IconEnum.BLOCKED}>
                        <TextInput
                            type={'password'}
                            placeholder={'Podaj swoje hasło...'}
                            onChange={({ target: { value } }) => setPassword(value)}
                            value={password}
                            required
                        />
                    </InputContainer>
                    <Button actionIcon={IconEnum.SEND}>Zaloguj się do skanera</Button>
                    {/* {platform === Platform.Web &&
                    <RegisterLink>
                        <Link to="/create-account-purchase-app">
                            Wykup roczną subskrypcję Skanera Okazji
                        </Link>
                    </RegisterLink>
                } */}
                    {platform === Platform.IOS && <RegisterLink>
                        <Link to="/create-account-purchase-app-ios">
                            Wykup roczną subskrypcję Skanera Okazji
                        </Link>
                    </RegisterLink>}
                    {<ResetPasswordWrapper onClick={() => setShowModal(true)}>
                        Zapomniałeś hasła? Resetuj hasło
                    </ResetPasswordWrapper> }
                </LoginForm>
                {user.isLoading && <Indicator />}
            </Container>

            <Modal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setSendPasswordEmail('');
                }}
                centeredContent
                backgroundColor={user.isDarkMode ? 'var(--color-alt-hover)' : 'var(--color-white)'}
                strictContentHeight
            >
                <div className={clsx({ light: !user.isDarkMode, dark: user.isDarkMode })}>
                    <ModalFormContainer>
                        <h1>Resetuj hasło</h1>
                        <InputContainer title="email" icon={IconEnum.EMAIL} required>
                            <TextInput
                                value={sendPasswordEmail}
                                onChange={(e) => setSendPasswordEmail(e.target.value)}
                                placeholder="Wpisz swój email..."
                                required
                            />
                        </InputContainer>
                        <Button onClick={handleSendResetLink}>
                            Wyślij
                        </Button>

                    </ModalFormContainer>
                </div>
            </Modal>
        </>
    );
};

export default LoginPage;
